// frontend/src/pages/SignUp.jsx
// Public company sign-up — the web equivalent of the mobile app's onboarding
// wizard (Welcome → CreateCompany → AdminSetup), collapsed into one form.
// Hits the same /api/company/register endpoint the mobile app already uses.

import React, { useState, useCallback, useRef } from 'react';
import {
  Paper, TextField, Button, Typography, Box, Alert, CircularProgress,
  Checkbox, FormControlLabel, Link as MuiLink, Grid, InputAdornment, IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Cancel } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import companyAPI from '../api/companyAPI';

// Legal pages live on the marketing site, not in this SPA — linking them at a
// bare "/privacy" hit the SPA fallback and bounced the user to /login.
// Env-overridable so a white-labelled deploy can point at its own policies.
const LEGAL_BASE = process.env.REACT_APP_LEGAL_BASE_URL || 'https://narevek.com';
const LEGAL_PRIVACY_URL = `${LEGAL_BASE}/pages/privacy.html`;
const LEGAL_TERMS_URL = `${LEGAL_BASE}/pages/terms.html`;

const EMPTY_FORM = {
  company_name: '', company_email: '', company_phone: '', company_city: '', company_state: '', company_gst: '',
  admin_name: '', admin_email: '', admin_password: '', confirm_password: '',
};

export default function SignUp({ onSignedUp }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [emailAvailable, setEmailAvailable] = useState(null); // null | true | false
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [contactConsent, setContactConsent] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const emailCheckTimer = useRef(null);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const checkEmail = useCallback((email) => {
    clearTimeout(emailCheckTimer.current);
    if (!email || !email.includes('@')) { setEmailAvailable(null); return; }
    emailCheckTimer.current = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const res = await companyAPI.checkEmail(email);
        setEmailAvailable(res.available);
      } catch {
        setEmailAvailable(null);
      } finally {
        setCheckingEmail(false);
      }
    }, 500);
  }, []);

  const handleCompanyEmailChange = (e) => {
    set('company_email')(e);
    checkEmail(e.target.value);
  };

  // The form sets noValidate, which turns off the browser's own type=email
  // check, so format has to be checked here too — the API rejects it as well,
  // but catching it before submit is a better experience than a round trip.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const validate = () => {
    if (!form.company_name.trim()) return 'Company name is required';
    if (!form.company_email.trim()) return 'Company email is required';
    if (!EMAIL_RE.test(form.company_email.trim())) return 'Please enter a valid company email address';
    if (emailAvailable === false) return 'This company email is already registered';
    if (!form.admin_name.trim()) return 'Your name is required';
    if (!form.admin_email.trim()) return 'Your email is required';
    if (!EMAIL_RE.test(form.admin_email.trim())) return 'Please enter a valid email address';
    if (!form.admin_password || form.admin_password.length < 6) return 'Password must be at least 6 characters';
    if (form.admin_password !== form.confirm_password) return 'Passwords do not match';
    if (!agreedToTerms) return 'You must agree to the Terms & Conditions to continue';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError('');
    try {
      const data = await companyAPI.register({
        ...form,
        accepted_terms: agreedToTerms,
        contact_consent: contactConsent,
      });
      onSignedUp(data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', py: 4 }}>
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 560, p: { xs: 3, sm: 5 }, borderRadius: 3, boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box component="img" src="/images/brand/narevek-crm-logo-mark.png" alt="Narevek CRM" sx={{ width: 180 }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>Create your company</Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, mb: 3, textAlign: 'center' }}>
          Set up your own Narevek CRM workspace — leads, quotations, vendors, and more.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>Company details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Company Name" value={form.company_name} onChange={set('company_name')} disabled={loading} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Company Email" type="email"
                value={form.company_email} onChange={handleCompanyEmailChange} disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {checkingEmail ? <CircularProgress size={18} /> :
                        emailAvailable === true ? <CheckCircle color="success" fontSize="small" /> :
                        emailAvailable === false ? <Cancel color="error" fontSize="small" /> : null}
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Company Phone" value={form.company_phone} onChange={set('company_phone')} disabled={loading} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="City" value={form.company_city} onChange={set('company_city')} disabled={loading} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="State" value={form.company_state} onChange={set('company_state')} disabled={loading} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="GST Number (optional)" value={form.company_gst} onChange={set('company_gst')} disabled={loading} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Your admin account</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Your Name" value={form.admin_name} onChange={set('admin_name')} disabled={loading} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Your Email" type="email" value={form.admin_email} onChange={set('admin_email')} disabled={loading} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Password" type={showPwd ? 'text' : 'password'}
                value={form.admin_password} onChange={set('admin_password')} disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPwd(s => !s)} edge="end" size="small">
                        {showPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Confirm Password" type={showPwd ? 'text' : 'password'} value={form.confirm_password} onChange={set('confirm_password')} disabled={loading} />
            </Grid>
          </Grid>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={<Checkbox checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} disabled={loading} />}
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <MuiLink href={LEGAL_PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</MuiLink>
                {' '}and{' '}
                <MuiLink href={LEGAL_TERMS_URL} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</MuiLink>
              </Typography>
            }
          />

          {/* Separate and optional. Agreeing to terms of service is not
              agreement to receive sales calls, and India's DND rules make
              that distinction the customer's exposure, not ours. Unticked by
              default — a pre-ticked box is not consent. */}
          <FormControlLabel
            sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5 }}
            control={
              <Checkbox
                checked={contactConsent}
                onChange={e => setContactConsent(e.target.checked)}
                disabled={loading}
                sx={{ pt: 0.5 }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Narevek may contact me by phone or email about my trial and this product.
              </Typography>
            }
          />

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          <Button
            type="submit" fullWidth variant="contained" size="large" disabled={loading}
            sx={{
              mt: 3, mb: 2, py: 1.4, fontWeight: 700, fontSize: 16, textTransform: 'none',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Create Company'}
          </Button>

          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            Already have an account? <MuiLink component={RouterLink} to="/login">Sign in</MuiLink>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
